
import React, { useState, useEffect } from 'react';
import { format } from "date-fns";
import { CalendarIcon, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fetchPractitionerBookings, fetchPractitionerAvailability, isTimeSlotCompletelyAvailable, isDateBlackedOut } from "@/utils/booking-helpers";
import { getUserTimezone } from '@/utils/timezone-helpers';
import { formatInTimeZone } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';

interface DateTimeSelectorProps {
  selectedDate: Date | undefined;
  onDateChange: (date: Date | undefined) => void;
  selectedTime: string;
  onTimeChange: (time: string) => void;
  duration: number;
  practitionerId?: string;
  userTimezone?: string;
}

const generateTimeSlots = (selectedDate?: Date, availabilityRules?: any, practitionerTimezone?: string) => {
  // Default time range if no availability rules
  let startHour = 9;
  let endHour = 17;

  // If we have availability rules and a selected date, use the practitioner's working hours
  if (selectedDate && availabilityRules?.weekly_schedule && practitionerTimezone) {
    try {
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dateInPractitionerTz = new Date(selectedDate.toLocaleString('en-US', { timeZone: practitionerTimezone }));
      const dayOfWeek = dateInPractitionerTz.getDay();
      const dayName = dayNames[dayOfWeek];
      const daySchedule = availabilityRules.weekly_schedule[dayName];

      if (daySchedule?.enabled && daySchedule?.slots && daySchedule.slots.length > 0) {
        // Find the earliest start and latest end time across all slots for this day
        const allSlots = daySchedule.slots;
        const startTimes = allSlots.map((slot: any) => {
          const [hours] = slot.start.split(':').map(Number);
          return hours;
        });
        const endTimes = allSlots.map((slot: any) => {
          const [hours] = slot.end.split(':').map(Number);
          return hours;
        });

        startHour = Math.min(...startTimes);
        endHour = Math.max(...endTimes);
      }
    } catch (error) {
      console.error('Error determining working hours from availability rules:', error);
    }
  }

  const slots = [];
  // Generate slots in 15-minute intervals
  for (let hour = startHour; hour < endHour; hour++) {
    slots.push(`${hour.toString().padStart(2, '0')}:00`);
    slots.push(`${hour.toString().padStart(2, '0')}:15`);
    slots.push(`${hour.toString().padStart(2, '0')}:30`);
    slots.push(`${hour.toString().padStart(2, '0')}:45`);
  }
  // Add the final hour:00 slot (e.g., 17:00 if end is 17)
  slots.push(`${endHour.toString().padStart(2, '0')}:00`);

  return slots;
};

const DateTimeSelector: React.FC<DateTimeSelectorProps> = ({
  selectedDate,
  onDateChange,
  selectedTime,
  onTimeChange,
  duration,
  practitionerId,
  userTimezone
}) => {
  const { t, i18n } = useTranslation('booking');
  const isZh = i18n.language?.startsWith('zh');
  const [timeSlots, setTimeSlots] = React.useState<string[]>(generateTimeSlots());
  const [existingBookings, setExistingBookings] = useState<{start: string, end: string}[]>([]);
  const [availabilityRules, setAvailabilityRules] = useState<any>(null);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  const [isLoadingBookings, setIsLoadingBookings] = useState(false);
  const [practitionerTimezone, setPractitionerTimezone] = useState<string>('UTC');

  // Load availability rules immediately when practitionerId is available
  useEffect(() => {
    const loadAvailabilityData = async () => {
      if (practitionerId) {
        setIsLoadingAvailability(true);
        try {
          const [availability, timezone] = await Promise.all([
            fetchPractitionerAvailability(practitionerId),
            getUserTimezone(practitionerId)
          ]);

          console.log('📅 DateTimeSelector - Availability Rules:', availability);
          console.log('📅 DateTimeSelector - Practitioner Timezone:', timezone);
          console.log('📅 DateTimeSelector - Weekly Schedule:', availability?.weekly_schedule);
          console.log('📅 DateTimeSelector - Blackout Dates:', availability?.blackout_dates);

          setAvailabilityRules(availability);
          setPractitionerTimezone(timezone);
        } catch (error) {
          console.error('Error loading availability data:', error);
        } finally {
          setIsLoadingAvailability(false);
        }
      }
    };

    loadAvailabilityData();
  }, [practitionerId]);

  // Regenerate time slots when date or availability rules change
  useEffect(() => {
    if (selectedDate && availabilityRules && practitionerTimezone) {
      const newTimeSlots = generateTimeSlots(selectedDate, availabilityRules, practitionerTimezone);
      console.log('📅 Regenerated time slots for selected date:', selectedDate, 'Slots:', newTimeSlots);
      setTimeSlots(newTimeSlots);
    } else {
      // Use default time slots if no date or rules available yet
      setTimeSlots(generateTimeSlots());
    }
  }, [selectedDate, availabilityRules, practitionerTimezone]);

  // Load bookings when both date and practitioner are selected
  useEffect(() => {
    const loadBookings = async () => {
      if (selectedDate && practitionerId) {
        setIsLoadingBookings(true);
        try {
          const bookings = await fetchPractitionerBookings(practitionerId, selectedDate);
          console.log('📅 DateTimeSelector - Existing Bookings:', bookings);
          setExistingBookings(bookings as {start: string, end: string}[]);
        } catch (error) {
          console.error('Error loading bookings:', error);
        } finally {
          setIsLoadingBookings(false);
        }
      }
    };

    loadBookings();
  }, [selectedDate, practitionerId]);

  // If the current selected time is no longer available after fetching data, reset it
  useEffect(() => {
    if (selectedTime && selectedDate && (existingBookings.length > 0 || availabilityRules)) {
      const { available } = isTimeSlotCompletelyAvailable(
        selectedTime, 
        selectedDate, 
        duration, 
        existingBookings, 
        availabilityRules,
        practitionerTimezone
      );
      if (!available) {
        onTimeChange('');
      }
    }
  }, [existingBookings, availabilityRules, selectedTime, selectedDate, duration, onTimeChange, practitionerTimezone]);

  const isDateDisabled = (date: Date) => {
    // Get current date in practitioner's timezone for accurate comparison
    const today = new Date();
    const todayInPractitionerTz = new Date(today.toLocaleString('en-US', { timeZone: practitionerTimezone }));
    todayInPractitionerTz.setHours(0, 0, 0, 0);
    
    // Convert the input date to practitioner's timezone for comparison
    const dateInPractitionerTz = new Date(date.toLocaleString('en-US', { timeZone: practitionerTimezone }));
    dateInPractitionerTz.setHours(0, 0, 0, 0);
    
    // Check if date is in the past in practitioner's timezone
    if (dateInPractitionerTz < todayInPractitionerTz) {
      console.log('📅 Date disabled - in the past (practitioner timezone):', date, 'Practitioner TZ:', practitionerTimezone);
      return true;
    }
    
    // Only apply availability-based checks if rules have loaded
    if (availabilityRules && isDateBlackedOut(date, availabilityRules, practitionerTimezone)) {
      console.log('📅 Date disabled - blacked out:', date);
      return true;
    }
    
    // Check if the day of week is disabled in availability rules
    if (availabilityRules?.weekly_schedule) {
      // Get day of week in practitioner's timezone
      const dateInPractitionerTz = new Date(date.toLocaleString('en-US', { timeZone: practitionerTimezone }));
      const dayOfWeek = dateInPractitionerTz.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayName = dayNames[dayOfWeek];
      
      const daySchedule = availabilityRules.weekly_schedule[dayName];
      if (daySchedule && !daySchedule.enabled) {
        console.log('📅 Date disabled - day not enabled in schedule:', date, dayName);
        return true;
      }
    }
    
    return false;
  };

  const handleTimeChange = (value: string) => {
    onTimeChange(value);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-medium">{t('selectAppointmentTime')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium flex items-center">
            <CalendarIcon className="mr-2 h-4 w-4 text-amber-600" />
            {t('date')}
          </label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={"outline"}
                className={`w-full justify-start text-left font-normal ${
                  !selectedDate && "text-muted-foreground"
                }`}
              >
                {selectedDate ? format(selectedDate, "PPP") : t('selectDate')}
                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={onDateChange}
                disabled={isDateDisabled}
                showOutsideDays={false}
                initialFocus
                className="p-3 pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium flex items-center">
            <Clock className="mr-2 h-4 w-4 text-amber-600" />
            {t('time')}
            {isLoadingAvailability && (
              <span className="ml-2 text-xs text-gray-500">({t('loadingAvailability')})</span>
            )}
          </label>
          {isLoadingBookings && selectedDate ? (
            <div className="text-sm text-gray-500 p-2 border rounded-md bg-gray-50">
              {t('loadingTimeSlots')}
            </div>
          ) : !selectedDate ? (
            <div className="text-sm text-gray-500 p-2 border rounded-md bg-gray-50">
              {t('selectDateFirst')}
            </div>
          ) : (
            <Select
              value={selectedTime}
              onValueChange={handleTimeChange}
              disabled={isLoadingAvailability}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={isLoadingAvailability ? t('loadingAvailability') : t('selectTime')} />
              </SelectTrigger>
              <SelectContent>
                 {timeSlots.map((time) => {
                   const { available, reason } = selectedDate ? 
                     isTimeSlotCompletelyAvailable(time, selectedDate, duration, existingBookings, availabilityRules, practitionerTimezone) : 
                     { available: true };
                   
                   const displayReason = reason === 'past' ? t('past') : 
                     reason === 'booked' ? t('booked') : 
                     reason === 'too_soon' ? t('tooSoon') : t('unavailable');
                   
                   // Create dual timezone display if user timezone is different
                   let timeDisplay = time;
                   let sublabel = '';
                   
                   if (userTimezone && practitionerTimezone && userTimezone !== practitionerTimezone && selectedDate) {
                     try {
                       // Create date object for this time slot in practitioner timezone
                       const [hours, minutes] = time.split(':').map(Number);
                       const slotDateTime = new Date(selectedDate);
                       slotDateTime.setHours(hours, minutes, 0, 0);
                       
                       // Format in user's timezone
                       const userTime = formatInTimeZone(slotDateTime, userTimezone, 'h:mm a');
                       
                       const practitionerTime = formatInTimeZone(slotDateTime, practitionerTimezone, 'h:mm a');

                       // 如果主显示想用“用户时间”，就把 timeDisplay 换成 userTime：
                       timeDisplay = userTime;
                                           
                       // sublabel 显示“医生时间”
                       sublabel = isZh ? `${practitionerTime} 疗愈师时间` : `${practitionerTime} practitioner's time`;
                      //  sublabel = `${userTime} your practitioner time`;
                     } catch (error) {
                       console.error('Error formatting dual timezone:', error);
                     }
                   }
                   
                   return (
                     <SelectItem 
                       key={time} 
                       value={time}
                       disabled={!available}
                       className={!available ? 'text-gray-400 line-through' : ''}
                     >
                       <div className="flex flex-col">
                         <span>
                           {timeDisplay} ({duration} {t('minutes')})
                           {!available && ` - ${displayReason}`}
                         </span>
                         {sublabel && (
                           <span className="text-xs text-neutral-500">{sublabel}</span>
                         )}
                       </div>
                     </SelectItem>
                   );
                 })}
              </SelectContent>
            </Select>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default DateTimeSelector;
